"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

// Chrome/Edge/Android expose this; Safari and Firefox don't -- absence just means "assume a
// normal connection," never blocks the video, so this degrades safely everywhere.
interface NetworkInformation {
  saveData?: boolean;
}

/**
 * A muted, looping background video with a black tint and a poster fallback -- used for the
 * home hero and the About section. Performance-conscious by construction, not as an
 * afterthought:
 *  - The poster (a real next/image, so it gets real optimization + can be the LCP candidate)
 *    always renders first and stays underneath; the <video> is a progressive enhancement layered
 *    on top, never something the page waits on.
 *  - The <video> itself only mounts once the poster has actually painted (via its onLoad), so
 *    its network request never competes with the poster or other above-the-fold resources for
 *    bandwidth during first paint.
 *  - `lazy` additionally gates mounting on IntersectionObserver, for a video further down the
 *    page (About) that has no business loading before the visitor scrolls near it.
 *  - Skipped entirely (poster-only, zero video bytes fetched) for prefers-reduced-motion and for
 *    browsers reporting Data Saver (navigator.connection.saveData) -- both real, standard
 *    signals for "don't autoplay video at this person," not a judgment call this component makes
 *    up on its own.
 */
export default function VideoBackdrop({
  webmSrc,
  mp4Src,
  poster,
  alt,
  priority,
  sizes = "100vw",
  tintClassName = "bg-black/45",
  lazy = false,
}: {
  webmSrc: string;
  mp4Src: string;
  poster: string;
  alt: string;
  priority?: boolean;
  sizes?: string;
  tintClassName?: string;
  lazy?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [nearViewport, setNearViewport] = useState(!lazy);
  // A lazy initializer, not an effect -- this reads stable browser APIs once at mount (same
  // pattern as AnnouncementBannerDisplay's dismissed check), so there's no extra render pass
  // just to flip this from a default to its real value.
  const [skipVideo] = useState(() => {
    if (typeof window === "undefined") return false;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const connection = (navigator as { connection?: NetworkInformation }).connection;
    return reducedMotion || Boolean(connection?.saveData);
  });
  // Picking the source ourselves, once, rather than handing the browser two <source> children
  // and letting it negotiate -- real-world traffic captures (Lighthouse's own network log
  // included) showed Chrome fetching *both* the webm and the mp4 for a single <video>, doubling
  // the transfer for no playback benefit. A single explicit src removes the ambiguity a
  // multi-<source> element leaves for the browser to resolve.
  const [videoSrc] = useState(() => {
    if (typeof document === "undefined") return mp4Src;
    const probe = document.createElement("video");
    return probe.canPlayType('video/webm; codecs="vp9"') ? webmSrc : mp4Src;
  });

  useEffect(() => {
    if (!lazy || nearViewport || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setNearViewport(true);
      },
      { rootMargin: "200px" }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [lazy, nearViewport]);

  const showVideo = posterLoaded && nearViewport && !skipVideo;

  return (
    <div ref={containerRef} className="absolute inset-0">
      <Image
        src={poster}
        alt={alt}
        fill
        priority={priority}
        sizes={sizes}
        className="object-cover"
        onLoad={() => setPosterLoaded(true)}
      />
      {showVideo && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          src={videoSrc}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className={`absolute inset-0 ${tintClassName}`} />
    </div>
  );
}
